import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { useNavigate } from 'react-router-dom';

import { apiService } from '@services/ApiService';
import { dateTimeService } from '@services/DateTimeService';
import { categoryRailStore } from '@services/CategoryRailStore';
import { syncService, actualizeWhileOnline } from '@services/SyncService';
import { type DashboardData } from '../models/SearchResult';
import { ImgShield } from '@components/Images/ImgShield';

import './_dashboard.scss';

const untitledNoteTitle = 'Untitled note';

/** Left color lane: the note's own color, exposed as a CSS var (falls back to the category color in SCSS). */
function getNoteStyle(color?: string | null): React.CSSProperties {
  if (!color || !color.match(/#[0-9A-Fa-f]{6}/g)) {
    return {};
  }
  return { '--note-color': color } as React.CSSProperties;
}

function relativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return dateTimeService.format(date, 'DD MMM, YYYY');
}

@observer
export default class Dashboard extends React.Component {
  @observable private _data: DashboardData | null = null;
  @observable private _isLoading = true;

  constructor(props: {}) {
    super(props);
    makeObservable(this);
  }

  componentDidMount(): void {
    // The shared rail (mounted in AppShell) is mode-aware; tell it we're in
    // dashboard mode and make sure categories are loaded.
    categoryRailStore.setMode('dashboard');
    void categoryRailStore.load();
    void this._load();
  }

  private async _load() {
    const data = await apiService.get<DashboardData>('/api/note/dashboard', { limit: '15' });
    action(() => {
      this._data = data;
      this._isLoading = false;
    })();
    syncService.markCached();
    actualizeWhileOnline(async () => {
      const fresh = await apiService.get<DashboardData>('/api/note/dashboard', { limit: '15' }, { actualize: true });
      action(() => { this._data = fresh; })();
    });
  }

  render(): JSX.Element {
    return <DashboardContent
      data={this._data}
      isLoading={this._isLoading}
    />;
  }
}

interface DashboardContentProps {
  data: DashboardData | null;
  isLoading: boolean;
}

function DashboardContent({ data, isLoading }: DashboardContentProps): JSX.Element {
  const navigate = useNavigate();
  const openNote = (noteId: string) => navigate('/n/' + noteId);

  // Category colors come from the shared rail (already loaded in dashboard mode) —
  // same source the All Notes list uses, so names and colors always agree.
  const catColorById: Record<string, string> = {};
  for (const c of categoryRailStore.categories) {
    catColorById[c.id] = c.color || '';
  }

  return (
    <div className="dashboard">
      <div className="dashboard-main">
        <div className="dashboard-inner">
          <h1 className="dashboard-title">Dashboard</h1>
          <p className="dashboard-subtitle">An overview of your notes and recent activity.</p>

          {isLoading && <div className="dashboard-loading">Loading…</div>}

          {!isLoading && data && (
            <>
              <div className="dashboard-stats">
                <div className="stat-card">
                  <span className="stat-value">{data.stats.total}</span>
                  <span className="stat-label">Notes</span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">{data.stats.categories}</span>
                  <span className="stat-label">Categories</span>
                </div>
                <div className="stat-card stat-accent">
                  <span className="stat-value">{data.stats.updatedLast7Days}</span>
                  <span className="stat-label">Edited in 7 days</span>
                </div>
                <div className="stat-card">
                  <span className="stat-value">{data.stats.archived}</span>
                  <span className="stat-label">Archived</span>
                </div>
              </div>

              <h2 className="dashboard-section-title">Recent notes</h2>

              {data.recent.length === 0 ? (
                <div className="dashboard-empty">No notes yet. Create one from the Notes page.</div>
              ) : (
                <div className="recent-list">
                  {data.recent.map(note => {
                    const catColor = note.categoryId ? catColorById[note.categoryId] || '' : '';
                    const itemStyle: React.CSSProperties = { ...getNoteStyle(note.color) };
                    if (catColor) (itemStyle as Record<string, string>)['--cat-color'] = catColor;
                    return (
                      <div
                        key={note.noteId}
                        className="recent-item"
                        style={itemStyle}
                        onClick={() => openNote(note.noteId)}
                      >
                        <div className="recent-item-body">
                          <div className="recent-item-title">
                            <span className="recent-item-title-text">{note.name || untitledNoteTitle}</span>
                            {note.isProtected && <span className="recent-item-protected"><ImgShield size={13} /></span>}
                          </div>
                          <div className="recent-item-meta">
                            {note.categoryName && (
                              <>
                                <span className="recent-item-category">
                                  <span className="ri-cat-dot" />
                                  {note.categoryName}
                                </span>
                                <span className="recent-item-sep">·</span>
                              </>
                            )}
                            <span>{relativeTime(note.lastWrite)}</span>
                          </div>
                          {note.snippet && <div className="recent-item-snippet">{note.snippet}</div>}
                        </div>
                        <div className="recent-item-date">{dateTimeService.format(new Date(note.lastWrite), 'DD MMM, HH:mm')}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>
    );
}
